import { useCallback, useLayoutEffect, useRef, useState } from "react";

export function createEvaluationRequestScope() {
  let activeScopeKey = null;
  const generations = new Map();

  const replaceScope = (scopeKey) => {
    activeScopeKey = scopeKey;
    generations.clear();
  };
  const deactivateScope = (scopeKey) => {
    if (activeScopeKey !== scopeKey) return;
    activeScopeKey = null;
    generations.clear();
  };
  const begin = (channel) => {
    if (!channel) throw new Error("Evaluation request channel is required");
    const scopeKey = activeScopeKey;
    if (scopeKey == null) throw new Error("Evaluation request scope is not active");
    const generation = (generations.get(channel) || 0) + 1;
    generations.set(channel, generation);
    const isCurrent = () => activeScopeKey === scopeKey && generations.get(channel) === generation;
    return Object.freeze({
      isCurrent,
      guard: (setter) => (value) => { if (isCurrent()) setter(value); },
    });
  };

  return { replaceScope, deactivateScope, begin };
}

export function useEvaluationRequestScope(scopeKey, resetState) {
  const managerRef = useRef(null);
  if (!managerRef.current) managerRef.current = createEvaluationRequestScope();
  const resetRef = useRef(resetState);
  resetRef.current = resetState;
  const [readyScopeKey, setReadyScopeKey] = useState(null);

  useLayoutEffect(() => {
    const manager = managerRef.current;
    manager.replaceScope(scopeKey);
    resetRef.current();
    setReadyScopeKey(scopeKey);
    return () => manager.deactivateScope(scopeKey);
  }, [scopeKey]);

  const beginRequest = useCallback((channel) => managerRef.current.begin(channel), []);
  return { ready: readyScopeKey === scopeKey, beginRequest };
}
